import { type RefObject, useLayoutEffect, useRef } from 'react'

export interface AnchoredPopoverOptions {
  open: boolean
  /** Горизонтальная привязка к якорю: левый или правый край. */
  align?: 'start' | 'end'
  /** Ширина попапа = ширине якоря (листбоксы селектов). */
  matchWidth?: boolean
}

export interface AnchoredPopover<Anchor extends HTMLElement, Pop extends HTMLElement> {
  anchorRef: RefObject<Anchor | null>
  popoverRef: RefObject<Pop | null>
  /** На popover-элемент вместе с popoverRef. */
  popoverProps: { popover: 'manual' }
}

// Якорный попап поверх нативного <dialog>: absolute-элемент внутри диалога
// раздувает его скролл-область (двигает layout), а createPortal в body рендерится
// ПОД top-layer'ом диалога. Popover API поднимает элемент в тот же top-layer,
// оставляя его в DOM-дереве (aria-connections по id продолжают работать);
// позиционируем fixed от rect'а якоря. popover="manual" — light-dismiss выключен,
// закрытие остаётся на логике компонента (Escape / клик вне).
export function useAnchoredPopover<Anchor extends HTMLElement, Pop extends HTMLElement>({
  open,
  align = 'start',
  matchWidth = false,
}: AnchoredPopoverOptions): AnchoredPopover<Anchor, Pop> {
  const anchorRef = useRef<Anchor>(null)
  const popoverRef = useRef<Pop>(null)

  // Layout-эффект: показать и спозиционировать до paint, без кадра «не там».
  useLayoutEffect(() => {
    // Widening до HTMLElement: narrowing generic-типизированных const не
    // доживает до замыкания position() (TS18047).
    const anchor: HTMLElement | null = anchorRef.current
    const pop: HTMLElement | null = popoverRef.current
    if (!open || !anchor || !pop) return

    // Optional call: среды без Popover API (тест-DOM) не роняют компонент.
    pop.showPopover?.()

    const GAP = 4
    // Стрелка, не function-декларация: hoisting ломает narrowing anchor/pop (TS18047).
    const position = () => {
      const rect = anchor.getBoundingClientRect()
      if (matchWidth) pop.style.width = `${rect.width}px`
      if (align === 'end') {
        pop.style.left = 'auto'
        pop.style.right = `${Math.max(0, window.innerWidth - rect.right)}px`
      } else {
        pop.style.left = `${rect.left}px`
        pop.style.right = 'auto'
      }
      // Флип вверх, когда снизу не помещается фактическая высота, а сверху — да.
      const height = pop.offsetHeight
      const spaceBelow = window.innerHeight - rect.bottom - GAP
      const openUp = height > spaceBelow && rect.top - GAP >= height
      if (openUp) {
        pop.style.top = 'auto'
        pop.style.bottom = `${window.innerHeight - rect.top + GAP}px`
      } else {
        pop.style.top = `${rect.bottom + GAP}px`
        pop.style.bottom = 'auto'
      }
    }

    position()
    window.addEventListener('resize', position)
    // capture: скролл тела <dialog> (и любого контейнера) не всплывает до window.
    document.addEventListener('scroll', position, true)
    return () => {
      window.removeEventListener('resize', position)
      document.removeEventListener('scroll', position, true)
      pop.hidePopover?.()
    }
  }, [open, align, matchWidth])

  return { anchorRef, popoverRef, popoverProps: { popover: 'manual' } }
}
